import moment from "moment";
import { Buffer, Cell, CellValue, Column, Row, Workbook, Worksheet } from "exceljs";
import ModuleCardList from "./module-card-list";
import Module from "./module";
import Msg from "./message";
import FieldPromote from "./field-promote";
import Field from "./field";
import ExcelParser from "./excel-parser";
import ExcelImportUtils from "./excel-import-utils";
import Entity from "./entity";
import Documents from "./documents";
import Api from "./api";
import { mutualInclusion, replaceAll } from "../utils";
import IConfigNumber from "../interface/field-cfg-number";
import IConfigDatetime from "../interface/field-cfg-datetime";
import IField from "../interface/field";
import IExcelImportResponse from "../interface/excel-import-response";
import IExcelImportMapField from "../interface/excel-import-map-field";
import IExcelImportMapColumn from "../interface/excel-import-map-column";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import Global from "../global";
import Message from "../enum/message";
import LogicalValue from "../enum/logical-value";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";
import DateType from "../enum/date-type";

class ExcelImportSingleton {

    /* Public Methods */

    public async getSheets(file: File): Promise<IExcelImportResponse> {
        const workbook: Workbook = new Workbook();
        const reader: FileReader = new FileReader();
        return new Promise((resolve: Function, reject: Function) => {
            reader.onload = () => {
                workbook.xlsx.load(reader.result as Buffer).then((wb: Workbook) => resolve({
                    fileName: file.name.split('.').slice(0, -1).join(''),
                    sheets: wb.worksheets.filter((ws: Worksheet) => {
                        return this.validSheet(ws);
                    })
                } as IExcelImportResponse), () => reject(ErrorCode.BadFile));
            };
            try {
                reader.readAsArrayBuffer(file);
            } catch (e) {
                reject(ErrorCode.BadFile);
            }
        });
    }

    public async parse(sheet: Worksheet, entityId: number = 0, singular: string = '', moduleId: number = 0): Promise<number> {
        if (this.validSheet(sheet)) {
            if (!moduleId) {
                moduleId = Module.getDefault().id as number;
            }
            if (!singular) {
                singular = sheet.name;
            }
            return this.parseSheet(sheet, entityId, singular, moduleId);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async parseMultiple(sheets: Worksheet[], moduleId: number = 0): Promise<void> {
        for (let i = 0; i < sheets.length; i++) {
            await this.parse(sheets[i], 0, '', moduleId);
        }
    }

    /* Private Methods */

    private async parseSheet(ws: Worksheet, entityId: number, singular: string, moduleId: number): Promise<number> {
        /* Trim empty columns and rows */
        const columns: Partial<Column>[] = [];
        ws.columns.slice(0, Global.EXCEL_SHEET_MAX_COLS).forEach((c: Partial<Column>) => {
            if (c.values && c.values.length && ExcelImportUtils.hasAnyValue([...c.values])) {
                columns.push(c);
            }
        });
        const rows: Row[] = [];
        ws.eachRow((row: Row) => {
            if (ExcelImportUtils.hasAnyValue(Object.values(row.values))) {
                rows.push(row);
            }
        });
        /* Define column types */
        const fields: Partial<IField>[] = [];
        const keys: string[] = [];
        columns.forEach((col: Partial<Column>) => {
            if (col.values && col.letter) {
                const key: string = col.letter;
                keys.push(col.letter);
                const values: CellValue[] = rows.map((r: Row) => ExcelImportUtils.getCellValue(r.getCell(key).value));
                const cells: Cell[] = rows.map((r: Row) => r.getCell(key));
                const field: Partial<IField> = Object.assign({
                    singular: this.getFieldName(values[0])
                }, ExcelParser.parseField(cells.slice(1)));
                field.systemType = Field.getSystemType(field.uiType as FieldType);
                fields.push(field);
            }
        });
        if (entityId) {
            return this.importIntoExisting(entityId, fields, keys, rows);
        } else {
            const entityId: number = await this.importIntoNew(singular, fields, keys, rows);
            await ModuleCardList.createByEntityId(entityId, moduleId);
            return entityId;
        }
    }

    private async importIntoNew(singular: string, fields: Partial<IField>[], keys: string[], rows: Row[]): Promise<number> {
        /* Create entity and map columns */
        const entity: IEntity = await Entity.create(singular, fields);
        const columnMapping: IExcelImportMapColumn[] = [];
        entity.fields.forEach((field: IField, index: number) => {
            fields[index].id = field.id;
            columnMapping.push({ letter: keys[index], target: field });
        });
        /* Create documents */
        const docs: IDocument[] = this.getDocuments(rows.slice(1), columnMapping);
        await Api.insertDocuments(entity.id, docs);
        return entity.id;
    }

    private async importIntoExisting(entityId: number, fields: Partial<IField>[], allKeys: string[], rows: Row[]): Promise<number> {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            /* Define fields mapping */
            const fieldMapping: IExcelImportMapField[] = [];
            const keys: string[] = [];
            fields.forEach((source: Partial<IField>, index: number) => {
                let promote: boolean = false;
                const target: IField | null = entity.fields.find((target: IField) => {
                    if (mutualInclusion(target.singular, source.singular as string)) {
                        const srcType: FieldType = source.uiType as FieldType;
                        const trgType: FieldType = target.uiType;
                        if (trgType === FieldType.linked && srcType === FieldType.text) {
                            promote = true;
                            return true;
                        } else {
                            return trgType === srcType;
                        }
                    }
                    return false;
                }) || null;
                if (target) {
                    keys.push(allKeys[index]);
                    fieldMapping.push({ source, target, promote, index });
                }
            });
            if (fieldMapping.length) {
                if (!!fieldMapping.find((m: IExcelImportMapField) => m.promote)) {
                    return await this.importWithBuffer(entity, fieldMapping, keys, rows);
                } else {
                    return await this.importWithoutBuffer(entity, fieldMapping, keys, rows);
                }
            } else {
                throw ErrorCode.ExcelImportNoMapping;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    private async importWithBuffer(entity: IEntity, fieldMapping: IExcelImportMapField[], keys: string[], rows: Row[]): Promise<number> {
        /* Create a buffer entity */
        const name: string = 'EXCEL_IMPORT_BUFFER';
        const fields: Partial<IField>[] = fieldMapping.map((m: IExcelImportMapField) => m.source);
        const bufferId: number = await this.importIntoNew(name, fields, keys, rows);
        let error: ErrorCode | null = null;
        try {
            /* Transform "text" type fields to "linked" fields */
            for (let i = 0; i < fieldMapping.length; i++) {
                const map: IExcelImportMapField = fieldMapping[i];
                if (map.promote) {
                    const field: IField = await FieldPromote.toLinked(map.source.id as number, {
                        entityId: map.target.foreignerEcn,
                        fieldId: map.target.foreignerFCNs[0]
                    });
                    map.source = Field.get(field.linkedRelation[0]) as IField;
                    map.target = Field.get(map.target.linkedRelation[0]) as IField;
                }
            }
            /* Map buffer documents to target entity documents */
            const sourceDocs: IDocument[] = await Documents.loadDocuments(bufferId);
            const targetDocs: IDocument[] = [];
            sourceDocs.forEach((d: IDocument) => {
                targetDocs.push(this.mapDocumentFields(d, fieldMapping))
            });
            await Api.insertDocuments(entity.id, targetDocs);
        } catch (e) {
            error = e as ErrorCode;
        } finally {
            /* Delete buffer entity */
            await Entity.delete(bufferId);
        }
        if (error) {
            throw error;
        }
        return entity.id;
    }

    private async importWithoutBuffer(entity: IEntity, fieldsMap: IExcelImportMapField[], keys: string[], rows: Row[]): Promise<number> {
        const columnMapping: IExcelImportMapColumn[] = [];
        fieldsMap.forEach((m: IExcelImportMapField, index: number) => {
            columnMapping.push({ letter: keys[index], target: m.target });
        });
        const docs: IDocument[] = this.getDocuments(rows.slice(1), columnMapping);
        await Api.insertDocuments(entity.id, docs);
        return entity.id;
    }

    private mapDocumentFields(doc: IDocument, map: IExcelImportMapField[]): IDocument {
        const result: IDocument = {};
        map.forEach((map: IExcelImportMapField) => {
            result[`c${map.target.id}`] = doc[`c${map.source.id}`];
        });
        return result;
    }

    private validSheet(ws: Worksheet): boolean {
        const columns: Partial<Column>[] = [];
        if (ws.columns) {
            ws.columns.slice(0, Global.EXCEL_SHEET_MAX_COLS).forEach((col: Partial<Column>) => {
                if (col.values && ExcelImportUtils.hasAnyValue([...col.values])) {
                    columns.push(col);
                }
            });
        }
        const rows: Row[] = [];
        ws.eachRow((row: Row) => {
            if (ExcelImportUtils.hasAnyValue(Object.values(row.values))) {
                rows.push(row);
            }
        });
        return !!columns.length && !!rows.length;
    }

    private getFieldName(name: CellValue): string {
        const defaultName: string = Msg.get(Message.defaultfieldname);
        if (typeof name === 'string') {
            return name.trim() || defaultName;
        }
        return defaultName;
    }

    private getDocuments(rows: Row[], columnMapping: IExcelImportMapColumn[]): IDocument[] {
        const result: IDocument[] = [];
        rows.forEach((row: Row) => {
            const doc: IDocument = {};
            columnMapping.forEach((columnMap: IExcelImportMapColumn) => {
                const key: string = columnMap.letter;
                const val: CellValue = ExcelImportUtils.getCellValue(row.getCell(key).value);
                if (ExcelImportUtils.hasValue(val)) {
                    let value: IDocumentValue = '';
                    const field: IField = columnMap.target;
                    if (field.uiType === FieldType.datetime) {
                        if (val instanceof Date) {
                            value = val.toISOString();
                        } else if (typeof val === 'string' && val.length) {
                            const config: IConfigDatetime = field.uiConfig as IConfigDatetime;
                            let mom: moment.Moment;
                            if (config.type === DateType.DateOnly) {
                                mom = moment.utc(val, config.format, true);
                            } else {
                                mom = moment(val, config.format, true);
                            }
                            if (mom.isValid()) {
                                value = mom.toISOString();
                            }
                        }
                    } else if (field.uiType === FieldType.number) {
                        if (typeof val === 'number') {
                            value = val as number;
                        } else if (typeof val === 'string' && val.length) {
                            const config: IConfigNumber = field.uiConfig as IConfigNumber;
                            let num: string = val;
                            num = replaceAll(num, config.decimalSeparator, 'A');
                            num = num.replace(/[\.\, ]/g, '');
                            num = replaceAll(num, 'A', '.');
                            const n: number = parseFloat(num);
                            if (!isNaN(n)) {
                                value = n;
                            }
                        }
                    } else if (field.uiType === FieldType.check) {
                        if (typeof val === 'string') {
                            if (ExcelParser.isCheckValueTrue(val)) {
                                doc[`c${field.id}`] = LogicalValue.True;
                            } else if (ExcelParser.isCheckValueFalse(val)) {
                                doc[`c${field.id}`] = LogicalValue.False;
                            }
                        }
                    } else if (field.uiType === FieldType.email) {
                        if (typeof val === 'string') {
                            doc[`c${field.id}`] = replaceAll(val, ExcelParser.EmailLinkPrefix, '').toLowerCase();
                        }
                    } else {
                        if (typeof val === 'number') {
                            value = val.toString();
                        } else if (typeof val === 'string' && val.length) {
                            value = val as string;
                        }
                    }
                    if (value !== '') {
                        doc[`c${field.id}`] = value;
                    }
                }
            });
            if (Object.values(doc).length) {
                result.push(doc);
            }
        });
        return result;
    }

}

const ExcelImport = new ExcelImportSingleton();

export default ExcelImport;